import { useEffect, useState, useRef, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'
import { RoutesApi } from '@/shared/api/routes'
import type { GeoJSON } from 'geojson'

export interface UsePublicLandsLayerProps {
  isEnabled: boolean
  getAccessToken: () => Promise<string | null>
  onLoadingChange?: (isLoading: boolean) => void
}

/**
 * Hook for managing public land boundaries layer rendering
 * Displays national parks, protected areas, and other public lands
 */
export function usePublicLandsLayer({
  isEnabled,
  getAccessToken,
  onLoadingChange
}: UsePublicLandsLayerProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const [publicLands, setPublicLands] = useState<any[]>([])
  const isLoadingRef = useRef(false)
  const routesApiRef = useRef<RoutesApi | null>(null)
  const boundsRef = useRef<mapboxgl.LngLatBounds | null>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const eventListenersAttachedRef = useRef(false)
  const initializedRef = useRef(false)
  const hoverPopupRef = useRef<mapboxgl.Popup | null>(null)
  const clickPopupRef = useRef<mapboxgl.Popup | null>(null)
  const lastFetchTimeRef = useRef<number>(0)
  const MIN_FETCH_INTERVAL = 5000 // Minimum 5 seconds between fetches to avoid rate limiting

  // Initialize API client
  useEffect(() => {
    if (!routesApiRef.current) {
      routesApiRef.current = new RoutesApi(getAccessToken)
    }
  }, [getAccessToken])

  // Fetch public lands for visible map bounds with progressive loading
  const fetchPublicLands = useCallback(async (bounds: mapboxgl.LngLatBounds, isInitial = false) => {
    if (!routesApiRef.current) {
      return
    }

    if (isLoadingRef.current && !isInitial) {
      return
    }

    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    
    const fullBbox: [number, number, number, number] = [
      sw.lng, // minLng
      sw.lat, // minLat
      ne.lng, // maxLng
      ne.lat  // maxLat
    ]

    // For initial load, fetch a smaller area first (1km radius) for faster response
    if (isInitial) {
      const center = bounds.getCenter()
      const radius = 0.009 // ~1km in degrees
      const quickBbox: [number, number, number, number] = [
        center.lng - radius,
        center.lat - radius,
        center.lng + radius,
        center.lat + radius
      ]

      isLoadingRef.current = true
      onLoadingChange?.(true)
      
      // Start both requests in parallel - quick fetch for immediate feedback, full fetch for complete data
      const quickFetch = routesApiRef.current.getPublicLandBoundaries(quickBbox)
        .then((quickResponse) => {
          if (quickResponse && quickResponse.success && quickResponse.features) {
            setPublicLands(quickResponse.features)
          }
        })
        .catch((error) => {
          // Silently handle quick fetch errors - full fetch will still complete
        })

      // Full bounds fetch runs in parallel
      const fullFetch = routesApiRef.current.getPublicLandBoundaries(fullBbox)
        .then((response) => {
          if (response && response.success && response.features) {
            setPublicLands(response.features)
          }
        })
        .catch((error) => {
          // Error handled silently - user can retry by moving map
        })
        .finally(() => {
          isLoadingRef.current = false
          onLoadingChange?.(false)
        })

      // Wait for both to complete (but quick one will render first)
      await Promise.all([quickFetch, fullFetch])
      return
    }

    // For non-initial loads, just fetch full bounds
    isLoadingRef.current = true
    onLoadingChange?.(true)
    
    try {
      const response = await routesApiRef.current.getPublicLandBoundaries(fullBbox)
      
      if (response && response.success && response.features) {
        setPublicLands(response.features)
      } else {
        setPublicLands([])
      }
    } catch (error) {
      setPublicLands([])
    } finally {
      isLoadingRef.current = false
      onLoadingChange?.(false)
    }
  }, [])

  // Helper function to compare bounds (check if they're significantly different)
  const boundsChanged = useCallback((oldBounds: mapboxgl.LngLatBounds | null, newBounds: mapboxgl.LngLatBounds): boolean => {
    if (!oldBounds) {
      return true
    }

    const oldNE = oldBounds.getNorthEast()
    const oldSW = oldBounds.getSouthWest()
    const newNE = newBounds.getNorthEast()
    const newSW = newBounds.getSouthWest()

    // Consider bounds changed if any corner has moved by more than a threshold (e.g., 5% of the bounds size)
    // Increased from 1% to 5% to reduce API calls and avoid rate limiting
    const lngThreshold = Math.abs(oldNE.lng - oldSW.lng) * 0.05
    const latThreshold = Math.abs(oldNE.lat - oldSW.lat) * 0.05

    return (
      Math.abs(oldNE.lng - newNE.lng) > lngThreshold ||
      Math.abs(oldNE.lat - newNE.lat) > latThreshold ||
      Math.abs(oldSW.lng - newSW.lng) > lngThreshold ||
      Math.abs(oldSW.lat - newSW.lat) > latThreshold
    )
  }, [])

  // Debounced function to fetch public lands when map moves
  const handleMapMove = useCallback(() => {
    if (!map.current || !isEnabled) {
      return
    }

    const bounds = map.current.getBounds()
    if (!bounds) {
      return
    }

    // Rate limiting: Don't fetch if we've fetched recently
    const now = Date.now()
    if (now - lastFetchTimeRef.current < MIN_FETCH_INTERVAL) {
      return
    }

    // Debounce to avoid too many API calls
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      // Only fetch if bounds have changed significantly
      if (boundsChanged(boundsRef.current, bounds)) {
        // Check rate limit again before fetching
        const timeSinceLastFetch = Date.now() - lastFetchTimeRef.current
        if (timeSinceLastFetch < MIN_FETCH_INTERVAL) {
          return
        }
        
        boundsRef.current = bounds
        lastFetchTimeRef.current = Date.now()
        fetchPublicLands(bounds)
      }
    }, 2000) // 2 second debounce to avoid rate limiting
  }, [isEnabled, fetchPublicLands, boundsChanged])

  // Setup layer and event handlers
  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) {
      return
    }

    // Helper function to setup the layer
    const setupLayer = () => {
      if (!map.current) return

      if (!isEnabled) {
        initializedRef.current = false
        // Remove layer and source when disabled
        try {
          if (map.current.getLayer('public-lands-fill')) {
            map.current.removeLayer('public-lands-fill')
          }
          if (map.current.getLayer('public-lands-stroke')) {
            map.current.removeLayer('public-lands-stroke')
          }
          if (map.current.getSource('public-lands')) {
            map.current.removeSource('public-lands')
          }
          setPublicLands([])
          boundsRef.current = null
          
          // Remove popups
          if (hoverPopupRef.current) {
            hoverPopupRef.current.remove()
            hoverPopupRef.current = null
          }
          if (clickPopupRef.current) {
            clickPopupRef.current.remove()
            clickPopupRef.current = null
          }
        } catch (error) {
          // Ignore errors if layer/source doesn't exist
        }
        // Remove event listeners
        if (eventListenersAttachedRef.current) {
          map.current.off('moveend', handleMapMove)
          map.current.off('zoomend', handleMapMove)
          map.current.off('mouseenter', 'public-lands-fill', handleMouseEnter)
          map.current.off('mouseleave', 'public-lands-fill', handleMouseLeave)
          map.current.off('click', 'public-lands-fill', handleClick)
          eventListenersAttachedRef.current = false
        }
        return
      }

      // Prevent re-initialization if already initialized and enabled
      if (initializedRef.current && isEnabled) {
        return
      }

      initializedRef.current = true
      
      // Fetch public lands for initial bounds (with progressive loading)
      const bounds = map.current.getBounds()
      if (bounds) {
        boundsRef.current = bounds
        fetchPublicLands(bounds, true) // true = isInitial, triggers progressive loading
      }

      // Listen to map move events to update public lands (only if not already attached)
      if (!eventListenersAttachedRef.current) {
        map.current.on('moveend', handleMapMove)
        map.current.on('zoomend', handleMapMove)
        eventListenersAttachedRef.current = true
      }
    }

    // Trust styleLoaded from context - don't check isStyleLoaded() which can be false during animations
    // If context says style is loaded, proceed
    if (styleLoaded) {
      setupLayer()
    } else {
      // Wait for styleLoaded to become true - the effect will re-run
      return
    }

    return () => {
      if (map.current && eventListenersAttachedRef.current) {
        map.current.off('moveend', handleMapMove)
        map.current.off('zoomend', handleMapMove)
        map.current.off('mouseenter', 'public-lands-fill', handleMouseEnter)
        map.current.off('mouseleave', 'public-lands-fill', handleMouseLeave)
        map.current.off('click', 'public-lands-fill', handleClick)
        eventListenersAttachedRef.current = false
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [map, isLoaded, styleLoaded, isEnabled, fetchPublicLands, handleMapMove])

  // Event handlers for interactivity
  const handleMouseEnter = useCallback((e: mapboxgl.MapLayerMouseEvent) => {
    if (!map.current) return

    const feature = e.features?.[0]
    if (!feature || !feature.properties) return

    const name = feature.properties.name || feature.properties.protectionTitle || 'Public Land'
    
    // Remove any existing hover popup
    if (hoverPopupRef.current) {
      hoverPopupRef.current.remove()
    }

    // Create hover popup
    hoverPopupRef.current = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'bottom',
      offset: [0, -10]
    })
      .setLngLat(e.lngLat)
      .setHTML(`<div class="text-sm font-medium">${name}</div>`)
      .addTo(map.current)

    // Change cursor to pointer
    map.current.getCanvas().style.cursor = 'pointer'
  }, [])

  const handleMouseLeave = useCallback(() => {
    if (!map.current) return

    // Remove hover popup
    if (hoverPopupRef.current) {
      hoverPopupRef.current.remove()
      hoverPopupRef.current = null
    }

    // Reset cursor
    map.current.getCanvas().style.cursor = ''
  }, [])

  const handleClick = useCallback((e: mapboxgl.MapLayerMouseEvent) => {
    if (!map.current) return

    const feature = e.features?.[0]
    if (!feature || !feature.properties) return

    const props = feature.properties
    const name = props.name || props.protectionTitle || 'Public Land'
    const protectionTitle = props.protectionTitle || props.boundaryType || 'Protected Area'
    const owner = props.owner
    const operator = props.operator
    const access = props.access
    const protectClass = props.protectClass

    // Remove any existing click popup
    if (clickPopupRef.current) {
      clickPopupRef.current.remove()
    }

    // Build popup content
    let popupContent = `<div class="p-2 min-w-[200px]">
      <div class="font-semibold text-sm mb-2">${name}</div>
      <div class="text-xs text-gray-600 space-y-1">`

    if (protectionTitle) {
      popupContent += `<div><span class="font-medium">Type:</span> ${protectionTitle}</div>`
    }

    if (protectClass) {
      popupContent += `<div><span class="font-medium">IUCN Category:</span> ${protectClass}</div>`
    }

    if (owner) {
      popupContent += `<div><span class="font-medium">Owner:</span> ${owner}</div>`
    }

    if (operator) {
      popupContent += `<div><span class="font-medium">Operator:</span> ${operator}</div>`
    }

    if (access) {
      popupContent += `<div><span class="font-medium">Access:</span> ${access}</div>`
    }

    popupContent += `</div></div>`

    // Create click popup
    clickPopupRef.current = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
      anchor: 'bottom',
      offset: [0, -10]
    })
      .setLngLat(e.lngLat)
      .setHTML(popupContent)
      .addTo(map.current)
  }, [])

  // Update layer when public lands data changes
  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded || !isEnabled) {
      return
    }

    // Trust styleLoaded from context - don't check isStyleLoaded() which can be false during animations
    if (!styleLoaded) {
      return
    }

    try {
      const hasSource = !!map.current.getSource('public-lands')

      const geoJsonData = {
        type: 'FeatureCollection' as const,
        features: publicLands
      }

      if (hasSource) {
        const source = map.current.getSource('public-lands') as mapboxgl.GeoJSONSource
        source.setData(geoJsonData)
      } else {
        // Create source
        map.current.addSource('public-lands', {
          type: 'geojson',
          data: geoJsonData
        })

        // Create fill layer
        map.current.addLayer({
          id: 'public-lands-fill',
          type: 'fill',
          source: 'public-lands',
          paint: {
            'fill-color': 'rgba(34, 197, 94, 0.2)', // Semi-transparent green
            'fill-opacity': 0.2
          },
          minzoom: 8 // Only show at zoom 8 and above
        })

        // Create stroke layer
        map.current.addLayer({
          id: 'public-lands-stroke',
          type: 'line',
          source: 'public-lands',
          paint: {
            'line-color': '#22c55e', // Green
            'line-width': 2,
            'line-opacity': 0.8
          },
          minzoom: 8
        })

        // Add event handlers for interactivity
        map.current.on('mouseenter', 'public-lands-fill', handleMouseEnter)
        map.current.on('mouseleave', 'public-lands-fill', handleMouseLeave)
        map.current.on('click', 'public-lands-fill', handleClick)

        // Ensure public lands layers are below markers but above base map
        const layersToMoveAbove = ['dead-end-markers', 'waypoint-markers', 'poi-markers', 'forest-roads']
        layersToMoveAbove.forEach(layerId => {
          if (map.current?.getLayer(layerId)) {
            try {
              map.current.moveLayer('public-lands-stroke', layerId)
              map.current.moveLayer('public-lands-fill', layerId)
            } catch (e) {
              // Ignore if layer doesn't exist
            }
          }
        })
      }
    } catch (error) {
      // Silently handle errors - layer will retry on next update
    }
  }, [map, isLoaded, styleLoaded, isEnabled, publicLands, handleMouseEnter, handleMouseLeave, handleClick])
}

